import { api } from "./apiSlice";

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export interface BloodUnitForTesting {
  id: string;
  status: string;
  volume: number;
  bagType: string;
  expiryDate: string;
  donorName: string | null;
  donorBloodGroup: string | null;
  donationStartTime?: string | null;
}

export interface BloodTestRecord {
  id: string;
  bloodId: string;
  testDateTime: string;
  status: string;
  ABOTest: string;
  hivTest: boolean | null;
  hemoglobin: number;
  syphilis: boolean | null;
  hepatitisB: boolean | null;
  hepatitisC: boolean | null;
  malaria: boolean | null;
  resultPending: boolean;
}

export interface BloodTestCounts {
  untested_units: number;
  tests_done_this_month: number;
  tests_passed_this_month: number;
  failed_tests_this_month: number;
}

export const bloodTestApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getBloodTestCounts: builder.query<BloodTestCounts, void>({
      query: () => "/blood-test/counts",
      transformResponse: (res: ApiResponse<BloodTestCounts>) => res.data,
      providesTags: ["BloodTest"],
    }),
    getPendingUnitsByInventory: builder.query<BloodUnitForTesting[], string>({
      query: (inventoryId) => `/blood-test/${inventoryId}`,
      transformResponse: (res: ApiResponse<{ units: BloodUnitForTesting[] }>) => res.data.units,
      providesTags: ["BloodTest"],
    }),
    getBloodUnitForTesting: builder.query<BloodUnitForTesting, string>({
      query: (bloodId) => `/blood-test/unit/${bloodId}`,
      transformResponse: (res: ApiResponse<BloodUnitForTesting>) => res.data,
    }),
    getTestByBloodId: builder.query<BloodTestRecord, string>({
      query: (bloodId) => `/blood-test/test/${bloodId}`,
      transformResponse: (res: ApiResponse<BloodTestRecord>) => res.data,
      providesTags: (_r, _e, bloodId) => [{ type: "BloodTest", id: bloodId }],
    }),
    passBloodUnit: builder.mutation<BloodTestRecord, string>({
      // Fixed from GET to POST — see the backend's routes/bloodTest.ts note.
      query: (bloodId) => ({ url: `/blood-test/pass/${bloodId}`, method: "POST" }),
      transformResponse: (res: ApiResponse<BloodTestRecord>) => res.data,
      invalidatesTags: ["BloodTest", "Blood"],
    }),
    updateBloodType: builder.mutation<BloodTestRecord, { bloodId: string; aboTest: string }>({
      query: ({ bloodId, aboTest }) => ({ url: `/blood-test/type/${bloodId}`, method: "POST", body: { aboTest } }),
      transformResponse: (res: ApiResponse<BloodTestRecord>) => res.data,
      invalidatesTags: (_r, _e, { bloodId }) => [{ type: "BloodTest", id: bloodId }],
    }),
    updateSyphilisTest: builder.mutation<BloodTestRecord, { bloodId: string; syphilis: boolean }>({
      query: ({ bloodId, syphilis }) => ({ url: `/blood-test/syphilis/${bloodId}`, method: "POST", body: { syphilis } }),
      transformResponse: (res: ApiResponse<BloodTestRecord>) => res.data,
      invalidatesTags: (_r, _e, { bloodId }) => [{ type: "BloodTest", id: bloodId }],
    }),
    updateHivTest: builder.mutation<BloodTestRecord, { bloodId: string; hivTest: boolean }>({
      query: ({ bloodId, hivTest }) => ({ url: `/blood-test/hiv/${bloodId}`, method: "POST", body: { hivTest } }),
      transformResponse: (res: ApiResponse<BloodTestRecord>) => res.data,
      invalidatesTags: (_r, _e, { bloodId }) => [{ type: "BloodTest", id: bloodId }],
    }),
    updateHepatitisTest: builder.mutation<BloodTestRecord, { bloodId: string; hepatitisB: boolean; hepatitisC: boolean }>({
      query: ({ bloodId, ...body }) => ({ url: `/blood-test/hepatitis/${bloodId}`, method: "POST", body }),
      transformResponse: (res: ApiResponse<BloodTestRecord>) => res.data,
      invalidatesTags: (_r, _e, { bloodId }) => [{ type: "BloodTest", id: bloodId }],
    }),
    updateMalariaTest: builder.mutation<BloodTestRecord, { bloodId: string; malaria: boolean }>({
      query: ({ bloodId, malaria }) => ({ url: `/blood-test/malaria/${bloodId}`, method: "POST", body: { malaria } }),
      transformResponse: (res: ApiResponse<BloodTestRecord>) => res.data,
      invalidatesTags: (_r, _e, { bloodId }) => [{ type: "BloodTest", id: bloodId }],
    }),
    updateHemoglobin: builder.mutation<BloodTestRecord, { bloodId: string; hemoglobin: number }>({
      query: ({ bloodId, hemoglobin }) => ({ url: `/blood-test/hemoglobin/${bloodId}`, method: "POST", body: { hemoglobin } }),
      transformResponse: (res: ApiResponse<BloodTestRecord>) => res.data,
      invalidatesTags: (_r, _e, { bloodId }) => [{ type: "BloodTest", id: bloodId }],
    }),
  }),
});

export const {
  useGetBloodTestCountsQuery,
  useGetPendingUnitsByInventoryQuery,
  useGetBloodUnitForTestingQuery,
  useGetTestByBloodIdQuery,
  usePassBloodUnitMutation,
  useUpdateBloodTypeMutation,
  useUpdateSyphilisTestMutation,
  useUpdateHivTestMutation,
  useUpdateHepatitisTestMutation,
  useUpdateMalariaTestMutation,
  useUpdateHemoglobinMutation,
} = bloodTestApi;
